import { FunctionComponent, ReactElement, useState } from "react";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
    styled,
} from "@mui/material";

import { Delete, Edit } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";

import {
    useCreateIngredientCategory,
    useDeleteIngredientCategory,
    useGetIngredientCategories,
    useUpdateIngredientCategory,
} from "../../hooks";
import { useMessage } from "../../sdk/hooks";
import { IIngredientCategory } from "../../sdk/types/api";
import { ingredientQueryKeys } from "../ingredients/constants";

const Root = styled("section")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};

    padding: ${({ theme }) => theme.spacing(1)};
`;

const TitleContainer = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: center;

    padding: ${({ theme }) => theme.spacing(2, 1)};
`;

const Title = styled(Typography)`
    font-size: 24px;
    font-weight: 500;
`;

const Fields = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};

    padding-top: ${({ theme }) => theme.spacing(1)};
`;

interface ICategoryDraft {
    id: string | null;
    name: string;
    icon: string;
    position: string;
}

const emptyDraft: ICategoryDraft = { id: null, name: "", icon: "", position: "0" };

export const IngredientCategoriesScreen: FunctionComponent = (): ReactElement => {
    const { showError, showSuccess } = useMessage();
    const categoriesQuery = useGetIngredientCategories(ingredientQueryKeys.categories);
    const createMutation = useCreateIngredientCategory(ingredientQueryKeys.categories);
    const updateMutation = useUpdateIngredientCategory(ingredientQueryKeys.categories);
    const deleteMutation = useDeleteIngredientCategory(ingredientQueryKeys.categories);

    const [draft, setDraft] = useState<ICategoryDraft | null>(null);

    const handleSave = async () => {
        if (!draft) {
            return;
        }
        const body = {
            name: draft.name.trim(),
            icon: draft.icon.trim() || null,
            position: Number(draft.position) || 0,
        };
        try {
            if (draft.id) {
                await updateMutation.mutateAsync({ categoryId: draft.id, body });
            } else {
                await createMutation.mutateAsync(body);
            }
            showSuccess("Category saved");
            setDraft(null);
        } catch (error) {
            showError(error);
        }
    };

    const handleDelete = async (category: IIngredientCategory) => {
        if (!window.confirm(`Delete "${category.name}"? Its ingredients move to "Other".`)) {
            return;
        }
        try {
            await deleteMutation.mutateAsync({ categoryId: category.id });
            showSuccess("Category deleted");
        } catch (error) {
            showError(error);
        }
    };

    const columns: GridColDef<IIngredientCategory>[] = [
        { field: "position", headerName: "Order", flex: 0.2 },
        { field: "icon", headerName: "Icon", flex: 0.2 },
        { field: "name", headerName: "Name", flex: 1 },
        { field: "slug", headerName: "Slug", flex: 0.8 },
        {
            field: "actions",
            headerName: "",
            flex: 0.3,
            sortable: false,
            renderCell: ({ row }: GridRenderCellParams<IIngredientCategory>) => (
                <>
                    <IconButton
                        size="small"
                        onClick={() =>
                            setDraft({
                                id: row.id,
                                name: row.name,
                                icon: row.icon ?? "",
                                position: String(row.position),
                            })
                        }
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(row)}>
                        <Delete fontSize="small" />
                    </IconButton>
                </>
            ),
        },
    ];

    return (
        <Root>
            <TitleContainer>
                <Title>Ingredient categories</Title>
                <Button variant="contained" onClick={() => setDraft(emptyDraft)}>
                    New category
                </Button>
            </TitleContainer>

            <DataGrid
                autoHeight
                rows={categoriesQuery.data ?? []}
                columns={columns}
                loading={categoriesQuery.isLoading}
                disableRowSelectionOnClick
                disableColumnSelector
                hideFooter
            />

            <Dialog open={Boolean(draft)} onClose={() => setDraft(null)} fullWidth maxWidth="xs">
                <DialogTitle>{draft?.id ? "Edit category" : "New category"}</DialogTitle>
                <DialogContent>
                    <Fields>
                        <TextField
                            label="Name"
                            size="small"
                            value={draft?.name ?? ""}
                            onChange={(event) => setDraft((current) => current && { ...current, name: event.target.value })}
                        />
                        <TextField
                            label="Icon (emoji)"
                            size="small"
                            value={draft?.icon ?? ""}
                            onChange={(event) => setDraft((current) => current && { ...current, icon: event.target.value })}
                        />
                        <TextField
                            label="Order in the app"
                            size="small"
                            type="number"
                            value={draft?.position ?? "0"}
                            onChange={(event) =>
                                setDraft((current) => current && { ...current, position: event.target.value })
                            }
                        />
                    </Fields>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDraft(null)}>Cancel</Button>
                    <LoadingButton
                        variant="contained"
                        disabled={!draft?.name.trim()}
                        loading={createMutation.isLoading || updateMutation.isLoading}
                        onClick={handleSave}
                    >
                        Save
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Root>
    );
};
